import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  format,
  startOfYear,
  endOfYear,
  addHours,
  endOfMonth,
  startOfMonth,
} from "date-fns";
import { ptBR } from "date-fns/locale";

import { CardManagement } from "../../../../components/CardManagement";
import { Empty } from "../../../../components/Empty";
import { HeaderTitle } from "../../../../components/HeaderTitle";
import { useAuth } from "../../../../hooks/useAuth";
import { useNotification } from "../../../../hooks/useNotification";
import { getAllManagements } from "../../../../services/management";
import { ICardManagementProps, IManagementProps } from "../../interfaces";

import { Spin, Filter, DateFiltered } from "./styles";
import { useTranslate } from "../../../../hooks/useTranslate";
import { formatDateToAPI } from "../../../../utils/format-date";
import { IconButton, Paper, Stack } from "@mui/material";
import { Add, FilterList } from "@mui/icons-material";
import { Link } from "react-router-dom";

const ListManagements = () => {
  const { errorNotification } = useNotification();
  const { propertiesIds, user } = useAuth();
  const { translate } = useTranslate();

  const [managements, setManagements] = useState<ICardManagementProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(false);
  const [countItemsFiltered, setCountItemsFiltered] = useState(0);
  const [dateFiltered, setDateFiltered] = useState("");

  const boot = useCallback(
    async (date: Date, type: "year" | "month") => {
      try {
        setLoading(true);
        const id_farm = propertiesIds[0];
        const id_user = user.id;
        let fromDate = formatDateToAPI(startOfYear(date));
        let toDate = formatDateToAPI(endOfYear(date));

        if (type === "month") {
          fromDate = formatDateToAPI(startOfMonth(date));
          toDate = formatDateToAPI(endOfMonth(date));
        }

        const managements: IManagementProps[] = await getAllManagements({
          id_farm,
          id_user,
          fromDate,
          toDate,
        });

        const managementsFormatted = managements.map((management) => ({
          id: management.id,
          title: management.nameManagement,
          farmer: management.author,
          date: new Date(management.created_at),
          total: management.attributes.animals.length,
          url: `/managements/${management.id}`,
        }));

        if (type === "month") {
          setCountItemsFiltered(managementsFormatted.length);
          setFilter(false);
        }
        setManagements(managementsFormatted);
      } catch {
        setCountItemsFiltered(0);
        errorNotification(translate("management.not_found_results"));
      } finally {
        setFilter(false);
        setLoading(false);
      }
    },
    [propertiesIds, user.id, errorNotification, translate]
  );

  const ready = useRef(false);

  useEffect(() => {
    if (propertiesIds.length > 0 && user.id && !ready.current) {
      ready.current = true;
      boot(new Date(), "year");
    }
  }, [boot, propertiesIds, user]);

  if (loading) return <Spin size="large" tip="Loading..." />;

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2}>
          <HeaderTitle title={translate("management.title_list")} />
          <IconButton component={Link} to="/managements/create" color="primary">
            <Add />
          </IconButton>
        </Stack>

        {managements.length > 0 && (
          <IconButton
            color="primary"
            onClick={() => {
              setFilter(!filter);
            }}
          >
            <FilterList />
          </IconButton>
        )}
      </Stack>

      {managements.length === 0 ? (
        <Empty
          title={translate("global.title_empty")}
          subTitle={translate("management.subtitle_empty")}
        />
      ) : (
        <Stack>
          {filter && (
            <Filter>
              <span>{translate("management.period")}</span>
              <input
                type="month"
                onChange={async (e) => {
                  const dateFormatted = addHours(new Date(e.target.value), 3);
                  setDateFiltered(
                    format(dateFormatted, "MMMM yyyy", { locale: ptBR })
                  );
                  await boot(dateFormatted, "month");
                }}
              />
            </Filter>
          )}
          {countItemsFiltered > 0 && (
            <DateFiltered>
              <p>{dateFiltered}</p>
              <span>{countItemsFiltered}</span>
            </DateFiltered>
          )}
          <Paper sx={{ p: 2 }}>
            {managements.map((item) => (
              <CardManagement key={item.id} {...item} />
            ))}
          </Paper>
        </Stack>
      )}
    </Stack>
  );
};

export { ListManagements };
